import type { ReactNode } from 'react';
import type { Question } from '../../lib/dossiers';

interface Props {
  question: Pick<Question, 'cle' | 'type' | 'libelle' | 'aide' | 'options' | 'obligatoire'>;
  valeur: unknown;
  onChange: (valeur: unknown) => void;
  disabled?: boolean;
  erreur?: string | null;
}

/**
 * Une question du questionnaire de dossier, selon son type. Le même composant
 * sert à la création et au formulaire global.
 */
export function ChampQuestion({ question, valeur, onChange, disabled, erreur }: Props) {
  const id = `q-${question.cle}`;
  const libelle = (
    <>
      {question.libelle}
      {question.obligatoire ? <span className="dos-requis" aria-hidden="true"> *</span> : null}
    </>
  );

  let saisie: ReactNode;
  switch (question.type) {
    case 'BOOLEEN':
      saisie = (
        <div className="dos-choix" role="radiogroup" aria-labelledby={`${id}-l`}>
          {[
            { v: true, l: 'Oui' },
            { v: false, l: 'Non' },
          ].map((o) => (
            <label key={o.l} className={`dos-pastille${valeur === o.v ? ' is-actif' : ''}`}>
              <input
                type="radio"
                name={id}
                checked={valeur === o.v}
                disabled={disabled}
                onChange={() => onChange(o.v)}
              />
              {o.l}
            </label>
          ))}
        </div>
      );
      break;
    case 'CHOIX':
      saisie =
        (question.options?.length ?? 0) <= 4 ? (
          <div className="dos-choix dos-choix--liste" role="radiogroup" aria-labelledby={`${id}-l`}>
            {question.options?.map((o) => (
              <label key={o.valeur} className={`dos-option${valeur === o.valeur ? ' is-actif' : ''}`}>
                <input
                  type="radio"
                  name={id}
                  checked={valeur === o.valeur}
                  disabled={disabled}
                  onChange={() => onChange(o.valeur)}
                />
                <span>
                  <strong>{o.libelle}</strong>
                  {o.aide ? <small>{o.aide}</small> : null}
                </span>
              </label>
            ))}
          </div>
        ) : (
          <select
            id={id}
            value={typeof valeur === 'string' ? valeur : ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value || null)}
          >
            <option value="">— Choisir —</option>
            {question.options?.map((o) => (
              <option key={o.valeur} value={o.valeur}>
                {o.libelle}
              </option>
            ))}
          </select>
        );
      break;
    case 'NOMBRE':
      saisie = (
        <input
          id={id}
          type="number"
          inputMode="numeric"
          value={typeof valeur === 'number' ? String(valeur) : ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
      break;
    case 'DATE':
      saisie = (
        <input
          id={id}
          type="date"
          value={typeof valeur === 'string' ? valeur : ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
      break;
    default:
      saisie = (
        <input
          id={id}
          type="text"
          maxLength={500}
          value={typeof valeur === 'string' ? valeur : ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }

  return (
    <div className={`field dos-question${erreur ? ' has-erreur' : ''}`}>
      <label id={`${id}-l`} htmlFor={question.type === 'BOOLEEN' || question.type === 'CHOIX' ? undefined : id}>
        {libelle}
      </label>
      {question.aide ? <p className="dos-aide">{question.aide}</p> : null}
      {saisie}
      {erreur ? <p className="dos-erreur">{erreur}</p> : null}
    </div>
  );
}
